'use client'

import { useEffect, useRef, useState } from 'react'

import { Button } from '@/core/components/form/button/button'
import ErrorToast from '@/core/components/error-toast'
import {
  recoverCreation,
  useCreationRecovery,
} from '@/features/studio-renew/services/studio-crud.service'
import { useRenewStore } from '@/features/studio-renew/store'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { Kind } from '@/features/studio-renew/store'

const NAMES: Record<Kind, string> = {
  world: '세계관',
  character: '캐릭터',
  status: '상태창',
  command: '명령어',
  chat: '작품',
}

export default function CreationRecoveryNotice({
  kind,
  onRecovered,
}: {
  kind?: Kind
  onRecovered?: (id: string) => void
}) {
  const owner = useRenewStore((state) => state.owner)
  const identity = useSessionStore((state) => state.identity)
  const requests = useCreationRecovery((state) => state.pending)
  const pending = requests.filter(
    (item) =>
      item.scope.owner === owner &&
      item.scope.identity === identity &&
      (!kind || item.kind === kind)
  )
  const [busy, setBusy] = useState<Kind | null>(null)
  const [error, setError] = useState('')
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  if (!pending.length) return null
  return (
    <div role="status" className="mb-5 space-y-3 border-b border-border pb-5">
      {pending.map((item) => (
        <div
          key={`${item.scope.owner}:${item.scope.identity}:${item.kind}`}
          className="flex flex-wrap items-center gap-3"
        >
          <p className="min-w-0 flex-1 text-c14 leading-relaxed text-t2">
            이전 {NAMES[item.kind]}{' '}
            {item.action === 'detach'
              ? '분리'
              : item.action === 'copy'
                ? '복사'
                : '생성'}{' '}
            결과를 먼저 확인해 주세요. 같은 요청을 확인해 중복 생성을 막아요.
          </p>
          <Button
            variant="secondary"
            size="sm"
            loading={busy === item.kind}
            disabled={busy !== null}
            onClick={() => {
              setBusy(item.kind)
              setError('')
              void recoverCreation(item.kind)
                .then((id) => {
                  if (
                    mounted.current &&
                    useRenewStore.getState().owner === owner &&
                    useSessionStore.getState().identity === identity
                  )
                    onRecovered?.(id)
                })
                .catch((cause: unknown) => {
                  if (mounted.current)
                    setError(
                      cause instanceof Error
                        ? cause.message
                        : '이전 생성 결과를 확인하지 못했어요.'
                    )
                })
                .finally(() => {
                  if (mounted.current) setBusy(null)
                })
            }}
          >
            이전 생성 결과 확인
          </Button>
        </div>
      ))}
      <ErrorToast message={error} />
    </div>
  )
}
